import {
  Component,
  OnInit,
  Output,
  EventEmitter,
  Input,
  OnChanges,
  SimpleChanges,
} from '@angular/core';
import {
  UntypedFormBuilder,
  UntypedFormControl,
  UntypedFormGroup,
  Validators,
} from '@angular/forms';
@Component({
  selector: 'lum-ui-virtual-calculator',
  templateUrl: './ui-virtual-calculator.component.html',
  styleUrls: ['./ui-virtual-calculator.component.scss'],
})
export class UiVirtualCalculatorComponent implements OnInit, OnChanges {
  @Input()
  value :number;

  @Output()
  valueChangeEmit = new EventEmitter<number>();

  form :UntypedFormGroup;

  operators = ['+', '-', '*', '/'];
  virtualNumbers :string[] = [
    'c',
    '7',
    '8',
    '9',
    '+',
    '4',
    '5',
    '6',
    '-',
    '1',
    '2',
    '3',
    '*',
    '0',
    '.',
    '=',
    '/',
  ];

  constructor(private fb :UntypedFormBuilder) {}

  ngOnInit() :void {
    this.initForm();
    this.listentToValueChanges();
  }

  ngOnChanges({ value } :SimpleChanges) :void {
    if (value?.currentValue === 0 && this.form) {
      this.quantityControl?.setValue(value?.currentValue);
    }
  }

  onAppendToInput(value :string) :void {
    const quantity = this.quantityControl;
    const currentValue = quantity?.value;

    if (this.operators.includes(value)) {
      this.onOperatorClick(value);

      return;
    }

    switch (value) {
      case 'c':
        this.onClearClick();

        return;
      case '=':
        this.onEqualsClick();

        return;
      case '.':
        if (currentValue && currentValue?.includes('.')) {
          return;
        }
        if (currentValue === 0) {
          quantity?.setValue('0.');

          return;
        }

        quantity?.setValue(`${currentValue}.`);

        return;

      case '0':
        if (currentValue === 0) {
          return;
        }

        break;
    }

    const newValue = currentValue === 0 ? value : `${currentValue}${value}`;
    quantity?.setValue(newValue);
  }

  onOperatorClick(operator :string) :void {
    const quantity = this.quantityControl;
    let currentValue = quantity?.value?.toString();

    if (currentValue === '0') {
      return;
    }

    // If last character is an operator, replace it with the new operator
    if (this.operators.includes(currentValue?.trim()?.slice(-1))) {
      currentValue = currentValue?.trim().slice(0, -1);
    }

    // Regular expression to match any operator (+, -, *, /) surrounded by spaces
    const operatorRegex = / [\+\-\*\/] /;

    if (operatorRegex.test(currentValue)) {
      this.onEqualsClick();
      currentValue = this.quantityControl?.value;
      quantity?.setValue(`${currentValue} ${operator} `);

      return;
    }

    quantity?.setValue(`${currentValue?.trim()} ${operator} `);
  }

  onEqualsClick() :void {
    const quantity = this.quantityControl;
    const expression = quantity?.value;
    if (!expression || expression.endsWith(' ')) {
      return;
    }

    quantity?.setValue(this.getEvaluatedExpression(expression));
  }

  onBackspaceClick() :void {
    const quantity = this.quantityControl;
    const currentValue = quantity?.value;

    if (currentValue === 0) {
      return;
    }

    const newValue = currentValue.toString()?.slice(0, -1);
    quantity?.setValue(newValue);

    if (newValue === '') {
      quantity?.setValue(0);
    }
  }

  onClearClick() :void {
    this.quantityControl?.setValue(0);
  }

  onDone() :void {
    this.onEqualsClick();
  }

  private initForm() :void {
    this.form = this.fb.group({
      quantity: [{ value: 0, disabled: true }, [Validators.required]],
    });
  }

  private listentToValueChanges() :void {
    this.quantityControl?.valueChanges.subscribe((value :number) => {
      // Check if the last character of the value is an operator
      const lastCharacter = value.toString()?.trim()?.slice(-1);
      if (this.operators.includes(lastCharacter)) {
        return;
      }
      this.valueChangeEmit.emit(this.getEvaluatedExpression(value.toString()));
    });
  }

  private getEvaluatedExpression(expression :string) :number {
    // Regular expression to remove leading zeros from the last operand
    const operandRegex = /(\d+(\.\d+)?)(?=\D*$)/g;
    expression = expression.replace(operandRegex, (match :string) =>
      parseFloat(match).toString(),
    );

    // Evaluate the expression using eval() (caution: using eval can be insecure in certain scenarios)
    try {
      return eval(expression);
    } catch (error) {
      console.error('Error evaluating expression:', error);
    }

    return 0;
  }

  get quantityControl() :UntypedFormControl {
    return this.form.get('quantity') as UntypedFormControl;
  }
}
